import { useState } from 'react'
import { motion } from 'framer-motion'
import { User, ChevronDown, ChevronUp, Save, AlertCircle, CheckCircle, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select } from '@/components/ui/Select'
import { userApi } from '@/services/api'
import toast from 'react-hot-toast'

interface ProfileSectionProps {
  title: string
  subtitle: string
}

export function ProfileSection({ title, subtitle }: ProfileSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // Profile form data
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    nationality: '',
    address: '',
    city: '',
    postal_code: '',
    currency: 'EUR'
  })
  
  const { t } = useTranslation()

  // Currency options
  const currencyOptions = [
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'STN', label: 'Dobra (Db)' }
  ]

  // Load profile data when expanding
  const handleExpand = async () => {
    if (!isExpanded) {
      setIsLoading(true)
      setError(null)
      setSuccess(null)
      
      try {
        // Load current profile data from API - /users/me returns user data directly
        const response = await userApi.getUserProfile()
        const profileData = response.data.data || response.data
        const profile = profileData?.profile || {}

        // /users/me returns user object directly with nested profile
        if (profileData) {
          setProfileData({
            first_name: profile.first_name || profileData.first_name || '',
            last_name: profile.last_name || profileData.last_name || '',
            date_of_birth: profile.date_of_birth || profileData.date_of_birth || '',
            nationality: profile.nationality || profileData.nationality || '',
            address: profile.address || profileData.address || '',
            city: profile.city || profileData.city || '',
            postal_code: profile.postal_code || profileData.postal_code || '',
            currency: profile.currency || profileData.currency || 'EUR'
          })
        }
      } catch (error: any) {
        console.error('Error loading profile:', error)
        setError(
          error.response?.data?.error || 
          error.message || 
          t('settings.profileLoadError', 'Failed to load profile information')
        )
      } finally {
        setIsLoading(false)
      }
    }
    setIsExpanded(!isExpanded)
  }

  // Handle profile form input changes
  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }))
    if (error) setError(null)
    if (success) setSuccess(null)
  }

  // Save profile data
  const handleSave = async () => {
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await userApi.updateUserProfile(profileData)
      setSuccess(t('settings.profileUpdateSuccess', 'Profile updated successfully!'))
      toast.success(t('settings.profileUpdateSuccess', 'Profile updated successfully!'))
    } catch (err: any) {
      console.error('Error updating profile:', err)
      setError(
        err.response?.data?.error || 
        err.message || 
        t('settings.profileUpdateError', 'Failed to update profile information')
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <motion.button
        onClick={handleExpand}
        className="w-full flex items-center space-x-3 p-4 hover:bg-gray-50 transition-colors"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.05 }}
      >
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <User className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 text-left">
          <h3 className="font-medium text-text-primary">{title}</h3>
          <p className="text-sm text-text-secondary">{subtitle}</p>
        </div>
        <div className="text-text-secondary">
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          ) : isExpanded ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </div>
      </motion.button>
      
      {/* Profile Form Dropdown */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="border-t border-gray-100 bg-gray-50"
        >
          <div className="p-4 space-y-4">
            {/* Success Message */}
            {success && (
              <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-primary">
                  {t('settings.firstName', 'First Name')}
                </label>
                <Input
                  type="text"
                  value={profileData.first_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('first_name', e.target.value)}
                  placeholder={t('settings.firstNamePlaceholder', 'Enter first name')}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-primary">
                  {t('settings.lastName', 'Last Name')}
                </label>
                <Input
                  type="text"
                  value={profileData.last_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('last_name', e.target.value)}
                  placeholder={t('settings.lastNamePlaceholder', 'Enter last name')}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Demographics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-primary">
                  {t('settings.dateOfBirth', 'Date of Birth')}
                </label>
                <Input
                  type="date"
                  value={profileData.date_of_birth}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('date_of_birth', e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-primary">
                  {t('settings.nationality', 'Nationality')}
                </label>
                <Input
                  type="text"
                  value={profileData.nationality}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('nationality', e.target.value)}
                  placeholder={t('settings.nationalityPlaceholder', 'Enter nationality')}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Currency Preference */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-text-primary">
                {t('settings.currency', 'Currency')}
              </label>
              <Select
                options={currencyOptions}
                value={profileData.currency}
                onChange={(value) => handleInputChange('currency', value)}
                placeholder={t('settings.currencyPlaceholder', 'Select your preferred currency')}
                disabled={isLoading}
              />
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary flex items-center space-x-2">
                <MapPin className="h-4 w-4" />
                <span>{t('settings.addressInformation', 'Address Information')}</span>
              </h4>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-primary">
                  {t('settings.address', 'Address')}
                </label>
                <Textarea
                  value={profileData.address}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('address', e.target.value)}
                  placeholder={t('settings.addressPlaceholder', 'Enter your full address')}
                  rows={3}
                  disabled={isLoading}
                  className="resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-primary">
                    {t('settings.city', 'City')}
                  </label>
                  <Input
                    type="text"
                    value={profileData.city}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('city', e.target.value)}
                    placeholder={t('settings.cityPlaceholder', 'Enter your city')}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-primary">
                    {t('settings.postalCode', 'Postal Code')}
                  </label>
                  <Input
                    type="text"
                    value={profileData.postal_code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('postal_code', e.target.value)}
                    placeholder={t('settings.postalCodePlaceholder', 'Enter postal code')}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSave}
                disabled={isLoading}
                className="px-6"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t('common.saving', 'Saving...')}</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Save className="h-4 w-4" />
                    <span>{t('common.save', 'Save')}</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  )
}
